import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import LoginModal from '@/components/Auth/LoginModal'

function RedgeGateIcon({ className = 'h-10 w-10' }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <rect x="4" y="8" width="40" height="32" rx="6" stroke="currentColor" strokeWidth="2.5" fill="none" />
      <path d="M4 16h40" stroke="currentColor" strokeWidth="2" opacity="0.3" />
      <circle cx="18" cy="28" r="5" fill="#22c55e" />
      <circle cx="30" cy="28" r="5" fill="#3b82f6" />
      <path d="M23 28h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 25.5l1.2-1.2" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M19 24.5v-1.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M28.5 25l1-1.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M31 24v-1.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function WhatsAppIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

function SmsIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 10h.01" />
      <path d="M12 10h.01" />
      <path d="M16 10h.01" />
    </svg>
  )
}

export default function Home() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [showLoginModal, setShowLoginModal] = useState(false)

  useEffect(() => {
    if (location.state?.showLogin) {
      setShowLoginModal(true)
    }
  }, [location.state])

  useEffect(() => {
    if (user && location.state?.from?.pathname === '/dashboard') {
      navigate('/dashboard', { replace: true })
    }
  }, [user, location.state, navigate])

  return (
    <div className="min-h-screen bg-white">

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2.5 group">
            <RedgeGateIcon className="h-8 w-8 text-gray-900 group-hover:text-blue-600 transition-colors" />
            <span className="text-lg font-bold tracking-tight text-gray-900">
              redge<span className="text-blue-600">Gate</span>
            </span>
          </Link>
          <div className="hidden sm:flex items-center gap-6 text-sm font-medium text-gray-600">
            <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-gray-900 transition-colors">How It Works</a>
            <Link to="/docs" className="hover:text-gray-900 transition-colors">Docs</Link>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button asChild size="sm">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setShowLoginModal(true)}>
                  Log in
                </Button>
                <Button size="sm" onClick={() => setShowLoginModal(true)}>
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/60 via-white to-white pointer-events-none" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-tr from-blue-100/40 via-green-50/30 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-1.5 text-sm text-green-700 font-medium mb-8">
            <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            SMS &amp; WhatsApp in one gateway
          </div>

          <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.1] mb-6">
            One gateway for
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
              all your messages
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Send SMS through your Android device and WhatsApp messages from a single dashboard &amp; API.
            No third-party fees — use your own numbers, your own devices.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {user ? (
              <Button asChild size="lg" className="px-8 text-base h-12 rounded-xl shadow-lg shadow-blue-500/20">
                <Link to="/dashboard">Open Dashboard</Link>
              </Button>
            ) : (
              <Button size="lg" className="px-8 text-base h-12 rounded-xl shadow-lg shadow-blue-500/20" onClick={() => setShowLoginModal(true)}>
                Start for free
              </Button>
            )}
            <Button asChild variant="outline" size="lg" className="px-8 text-base h-12 rounded-xl">
              <Link to="/docs">Read the docs</Link>
            </Button>
          </div>

          {/* Channel badges */}
          <div className="mt-16 flex items-center justify-center gap-8 sm:gap-12 text-gray-400">
            <div className="flex items-center gap-2 text-sm font-medium">
              <SmsIcon className="h-5 w-5 text-blue-500" />
              <span className="text-gray-600">SMS</span>
            </div>
            <div className="h-6 w-px bg-gray-200" />
            <div className="flex items-center gap-2 text-sm font-medium">
              <WhatsAppIcon className="h-5 w-5 text-green-500" />
              <span className="text-gray-600">WhatsApp</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-24 bg-gray-50/60">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to send at scale
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Dual-channel messaging, device management, and a developer-friendly API — all under your control.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: (
                  <WhatsAppIcon className="h-6 w-6 text-green-500" />
                ),
                bg: 'bg-green-50',
                title: 'WhatsApp Gateway',
                desc: 'Connect your WhatsApp account and send text, images, documents, and polls — through a simple API or the dashboard.',
              },
              {
                icon: (
                  <SmsIcon className="h-6 w-6 text-blue-500" />
                ),
                bg: 'bg-blue-50',
                title: 'SMS Gateway',
                desc: 'Turn any Android phone into an SMS gateway. Install the app, pair with the dashboard, and start sending.',
              },
              {
                icon: (
                  <svg className="h-6 w-6 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                ),
                bg: 'bg-violet-50',
                title: 'REST API',
                desc: 'Send messages, check delivery status, and manage devices programmatically. Full API docs included.',
              },
              {
                icon: (
                  <svg className="h-6 w-6 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                ),
                bg: 'bg-amber-50',
                title: 'Multi-Device',
                desc: 'Register multiple Android phones and WhatsApp accounts. Each runs independently — one going offline won\'t affect the others.',
              },
              {
                icon: (
                  <svg className="h-6 w-6 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                ),
                bg: 'bg-rose-50',
                title: 'Auto-Reconnect',
                desc: 'Built-in keepalive checks and automatic session recovery. If a device drops, redgeGate brings it back without intervention.',
              },
              {
                icon: (
                  <svg className="h-6 w-6 text-cyan-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                ),
                bg: 'bg-cyan-50',
                title: 'Webhooks',
                desc: 'Get delivery receipts and incoming message notifications pushed to your server in real time.',
              },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all">
                <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${f.bg} mb-4`}>
                  {f.icon}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Up and running in minutes
            </h2>
            <p className="text-gray-500 text-lg max-w-lg mx-auto">
              Three steps to start sending from your own devices.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                color: 'bg-blue-600',
                title: 'Create an account',
                desc: 'Sign up, grab your API key from the dashboard.',
              },
              {
                step: '2',
                color: 'bg-green-500',
                title: 'Connect a device',
                desc: 'Install the Android app for SMS, or scan a QR code to link WhatsApp.',
              },
              {
                step: '3',
                color: 'bg-violet-500',
                title: 'Send messages',
                desc: 'Use the dashboard UI or hit the REST API — single or bulk, text or media.',
              },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className={`inline-flex items-center justify-center h-12 w-12 rounded-full ${s.color} text-white text-lg font-bold mb-5`}>
                  {s.step}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-24 bg-gray-50/60">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-500 text-lg max-w-lg mx-auto">
              Start free, upgrade when you need more capacity.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 items-start">
            {/* Free */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900">Free</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-gray-900">$0</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="mt-3 text-sm text-gray-500">Get started with the basics.</p>

              <ul className="mt-8 space-y-3 text-sm text-gray-600 flex-1">
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  1 device
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  10 messages / day
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  100 messages / month
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  1 API key
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Basic support
                </li>
              </ul>

              {user ? (
                <Button asChild variant="outline" className="mt-8 w-full rounded-xl h-11">
                  <Link to="/dashboard">Current plan</Link>
                </Button>
              ) : (
                <Button variant="outline" className="mt-8 w-full rounded-xl h-11" onClick={() => setShowLoginModal(true)}>
                  Get started
                </Button>
              )}
            </div>

            {/* Pro — highlighted */}
            <div className="bg-white rounded-2xl border-2 border-blue-600 p-8 flex flex-col relative shadow-lg shadow-blue-500/10">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Most popular
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Pro</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-gray-900">$9.99</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="mt-3 text-sm text-gray-500">For growing teams and businesses.</p>

              <ul className="mt-8 space-y-3 text-sm text-gray-600 flex-1">
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Up to 10 devices
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  500 messages / day
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  10,000 messages / month
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  10 API keys
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Bulk send up to 1,000
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Priority support
                </li>
              </ul>

              {user ? (
                <Button asChild className="mt-8 w-full rounded-xl h-11">
                  <Link to="/checkout/Pro">Upgrade to Pro</Link>
                </Button>
              ) : (
                <Button className="mt-8 w-full rounded-xl h-11" onClick={() => setShowLoginModal(true)}>
                  Get started
                </Button>
              )}
            </div>

            {/* Enterprise */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900">Enterprise</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-gray-900">$49.99</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="mt-3 text-sm text-gray-500">Unlimited everything for scale.</p>

              <ul className="mt-8 space-y-3 text-sm text-gray-600 flex-1">
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Unlimited devices
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Unlimited messages
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Unlimited bulk send
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Unlimited API keys
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Custom integrations
                </li>
                <li className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Premium support
                </li>
              </ul>

              {user ? (
                <Button asChild variant="outline" className="mt-8 w-full rounded-xl h-11">
                  <Link to="/checkout/Enterprise">Upgrade</Link>
                </Button>
              ) : (
                <Button variant="outline" className="mt-8 w-full rounded-xl h-11" onClick={() => setShowLoginModal(true)}>
                  Get started
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to send?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-lg mx-auto">
            No per-message fees. No vendor lock-in. Your devices, your data, your gateway.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {user ? (
              <Button asChild size="lg" className="px-8 text-base h-12 rounded-xl bg-white text-gray-900 hover:bg-gray-100">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            ) : (
              <Button size="lg" className="px-8 text-base h-12 rounded-xl bg-white text-gray-900 hover:bg-gray-100" onClick={() => setShowLoginModal(true)}>
                Create free account
              </Button>
            )}
            <Button asChild variant="outline" size="lg" className="px-8 text-base h-12 rounded-xl border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white">
              <Link to="/download">Download Android app</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <RedgeGateIcon className="h-5 w-5 text-gray-400" />
            <span>&copy; {new Date().getFullYear()} redgeGate</span>
          </div>
          <div className="flex gap-6">
            <Link to="/docs" className="hover:text-gray-600 transition-colors">Docs</Link>
            <a href="#pricing" className="hover:text-gray-600 transition-colors">Pricing</a>
            <Link to="/support" className="hover:text-gray-600 transition-colors">Support</Link>
          </div>
        </div>
      </footer>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  )
}
